/**
 * Scheduled reports — the second tab, matching Backup Radar's.
 *
 * Their columns are name, enabled, recipients, report types, when, current
 * state, and actions for edit / duplicate / run now / history. Somebody
 * switching across looks for exactly that, so it is exactly that.
 *
 * THE RUN HISTORY IS SHOWN, not hidden behind a button. A schedule that
 * quietly stops sending is the failure nobody reports - the customer does not
 * complain about an email that never arrived, they just stop being monitored
 * in everybody's mind. Putting the last outcome on the row means noticing
 * costs nothing.
 *
 * "Run now" deliberately does NOT satisfy the day's scheduled send. Testing a
 * schedule must not cancel the thing being tested.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { errText, ago } from '../btApi';
import { Modal } from './Modal';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

type Cadence = 'daily' | 'weekdays' | 'weekly' | 'monthly';

interface Schedule {
  id: string;
  name: string;
  enabled: boolean;
  recipients: string[];
  reportIds: string[];
  cadence: Cadence;
  hour: number;
  weekday: number;
  dayOfMonth: number;
  windowDays: number;
  lastRunAtMs: number | null;
  cadenceText: string;
}

interface Run {
  id: string;
  scheduleId: string;
  name: string;
  ok: boolean;
  error: string | null;
  reports: string[];
  recipients: number;
  atMs: number;
  trigger: string;
}

interface ReportDef { id: string; label: string; group: string; unavailable?: string }

const listSchedules = call<{ orgId: string }, { schedules: Schedule[]; runs: Run[]; timeZone: string | null }>('listReportSchedules');
const saveSchedule = call<Record<string, unknown>, { ok: boolean; id: string }>('saveReportSchedule');
const deleteSchedule = call<{ orgId: string; id: string }, { ok: boolean }>('deleteReportSchedule');
const runNow = call<{ orgId: string; id: string }, { ok: boolean; sent: number; error: string | null }>('runReportScheduleNow');

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const blank = (): Partial<Schedule> => ({
  name: '', enabled: true, recipients: [], reportIds: [],
  cadence: 'weekly', hour: 8, weekday: 1, dayOfMonth: 1, windowDays: 7,
});

export function ReportSchedules({ orgId, reports, canManage }: {
  orgId: string;
  reports: ReportDef[];
  canManage: boolean;
}) {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [zone, setZone] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<Schedule> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await listSchedules({ orgId });
      setSchedules(r.schedules); setRuns(r.runs); setZone(r.timeZone);
    } catch (e) { setErr(errText(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [orgId]);

  const lastRunOf = (id: string) => runs.find((r) => r.scheduleId === id) ?? null;

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setErr(null); setMsg(null);
    try { await fn(); await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  return (
    <div>
      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner">{msg}</div>}

      <p className="panel-note" style={{ marginTop: 0 }}>
        One email per schedule, with a PDF for each report chosen. The window is
        relative &mdash; &ldquo;the last 7 days&rdquo; means the 7 days before each send,
        not a fortnight frozen on the day you set it up.
        {zone && <> Times are in <strong>{zone}</strong>.</>}
      </p>

      {loading ? <div className="empty">Loading…</div> : (
        <table className="mini-table">
          <thead>
            <tr>
              <th>Name</th><th>Reports</th><th>Recipients</th><th>When</th><th>Last run</th><th />
            </tr>
          </thead>
          <tbody>
            {schedules.length === 0 && (
              <tr><td colSpan={6} className="dim">
                No scheduled reports. An MSP normally promises a client one lands on a fixed day.
              </td></tr>
            )}
            {schedules.map((s) => {
              const last = lastRunOf(s.id);
              return (
                <tr key={s.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    {!s.enabled && <span className="tag tag-warn">paused</span>}
                  </td>
                  <td className="dim" style={{ fontSize: 12 }}>
                    {s.reportIds.map((id) => reports.find((r) => r.id === id)?.label ?? id).join(', ')}
                  </td>
                  <td className="dim" style={{ fontSize: 12 }}>{s.recipients.join(', ')}</td>
                  <td className="dim" style={{ fontSize: 12 }}>
                    {s.cadenceText}
                    <div>last {s.windowDays} days</div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {/* The whole reason the history exists. */}
                    {!last ? <span className="dim">never</span>
                      : last.ok
                        ? <span className="tag tag-ok">sent {ago(new Date(last.atMs).toISOString())}</span>
                        : <span className="tag tag-bad" title={last.error ?? ''}>failed</span>}
                    {last?.error && (
                      <div className="dim" style={{ fontSize: 11 }}>{last.error.slice(0, 120)}</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canManage && (
                      <>
                        <button className="btn-link" onClick={() => setDraft(s)}>edit</button>
                        <button className="btn-link" style={{ marginLeft: 8 }}
                                onClick={() => setDraft({ ...s, id: undefined, name: `${s.name} (copy)` })}>
                          duplicate
                        </button>
                        <button className="btn-link" style={{ marginLeft: 8 }} disabled={busy !== null}
                                onClick={() => void act('run' + s.id, async () => {
                                  const r = await runNow({ orgId, id: s.id });
                                  setMsg(`Sent ${r.sent} report(s) to ${s.recipients.length} recipient(s).`);
                                })}>
                          {busy === 'run' + s.id ? 'sending…' : 'run now'}
                        </button>
                        <button className="btn-link danger" style={{ marginLeft: 8 }} disabled={busy !== null}
                                onClick={() => {
                                  if (!window.confirm(`Delete the "${s.name}" schedule?`)) return;
                                  void act('del' + s.id, () => deleteSchedule({ orgId, id: s.id }));
                                }}>
                          delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {canManage && (
        <button className="btn" style={{ marginTop: 12 }} onClick={() => setDraft(blank())}>
          Create a scheduled report
        </button>
      )}

      {runs.length > 0 && (
        <>
          <h3 className="panel-title" style={{ fontSize: 13, marginTop: 22 }}>Recent sends</h3>
          <table className="mini-table">
            <thead><tr><th>When</th><th>Schedule</th><th>Reports</th><th>To</th><th>Result</th></tr></thead>
            <tbody>
              {runs.slice(0, 15).map((r) => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{new Date(r.atMs).toLocaleString()}</td>
                  <td>{r.name}{r.trigger === 'manual' && <span className="tag" style={{ marginLeft: 6 }}>manual</span>}</td>
                  <td className="dim" style={{ fontSize: 11 }}>{r.reports.join(', ')}</td>
                  <td className="dim" style={{ fontSize: 11 }}>{r.recipients}</td>
                  <td style={{ fontSize: 11 }}>
                    {r.ok ? <span className="tag tag-ok">sent</span> : <span className="tag tag-bad">failed</span>}
                    {r.error && <div className="dim">{r.error.slice(0, 140)}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {draft && (
        <ScheduleDialog
          orgId={orgId} draft={draft} reports={reports}
          onClose={() => setDraft(null)}
          onSaved={async () => { setDraft(null); await load(); }}
        />
      )}
    </div>
  );
}

function ScheduleDialog({ orgId, draft, reports, onClose, onSaved }: {
  orgId: string;
  draft: Partial<Schedule>;
  reports: ReportDef[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [d, setD] = useState<Partial<Schedule>>(draft);
  const [to, setTo] = useState((draft.recipients ?? []).join(', '));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (patch: Partial<Schedule>) => setD({ ...d, ...patch });
  const chosen = d.reportIds ?? [];
  const available = reports.filter((r) => !r.unavailable);

  const canSave = Boolean((d.name ?? '').trim()) && chosen.length > 0 && to.includes('@');

  async function save() {
    setBusy(true); setErr(null);
    try {
      await saveSchedule({
        orgId,
        ...(d.id ? { id: d.id } : {}),
        name: d.name, enabled: d.enabled !== false,
        recipients: to.split(',').map((s) => s.trim()).filter(Boolean),
        reportIds: chosen,
        cadence: d.cadence, hour: d.hour, weekday: d.weekday,
        dayOfMonth: d.dayOfMonth, windowDays: d.windowDays,
      });
      onSaved();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }

  return (
    <Modal title={d.id ? 'Edit scheduled report' : 'New scheduled report'} onClose={onClose} dirty={busy}>
      {err && <div className="banner banner-error">{err}</div>}

      <div className="row-form">
        <label className="field" style={{ flex: 1 }}>
          <span className="field-label">Name</span>
          <input className="field-input" value={d.name ?? ''} autoFocus
                 placeholder="Monthly compliance pack"
                 onChange={(e) => set({ name: e.target.value })} />
        </label>
      </div>

      <label className="field">
        <span className="field-label">Recipients</span>
        <input className="field-input" value={to} placeholder="ops@vtgtx.com, manager@vtgtx.com"
               onChange={(e) => setTo(e.target.value)} />
        <span className="field-hint">Comma separated.</span>
      </label>

      <div className="field-label" style={{ marginTop: 12 }}>Reports to include</div>
      <div className="sched-reports">
        {available.map((r) => (
          <label key={r.id} className="check-line">
            <input
              type="checkbox" checked={chosen.includes(r.id)}
              onChange={() => set({
                reportIds: chosen.includes(r.id)
                  ? chosen.filter((x) => x !== r.id)
                  : [...chosen, r.id],
              })}
            />
            <span>{r.label}</span>
          </label>
        ))}
      </div>

      <div className="row-form" style={{ marginTop: 12 }}>
        <label className="field">
          <span className="field-label">How often</span>
          <select className="field-input" value={d.cadence ?? 'weekly'}
                  onChange={(e) => set({ cadence: e.target.value as Cadence })}>
            <option value="daily">Every day</option>
            <option value="weekdays">Weekdays</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
          </select>
        </label>

        {d.cadence === 'weekly' && (
          <label className="field">
            <span className="field-label">On</span>
            <select className="field-input" value={d.weekday ?? 1}
                    onChange={(e) => set({ weekday: Number(e.target.value) })}>
              {DAYS.map((n, i) => <option key={n} value={i}>{n}</option>)}
            </select>
          </label>
        )}

        {d.cadence === 'monthly' && (
          <label className="field">
            <span className="field-label">Day of month</span>
            <select className="field-input" value={d.dayOfMonth ?? 1}
                    onChange={(e) => set({ dayOfMonth: Number(e.target.value) })}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {/* 28 is the cap on purpose: the 31st would skip February and four
                other months, silently. */}
            <span className="field-hint">Capped at 28 so no month is ever skipped.</span>
          </label>
        )}

        <label className="field">
          <span className="field-label">At</span>
          <select className="field-input" value={d.hour ?? 8}
                  onChange={(e) => set({ hour: Number(e.target.value) })}>
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {((h % 12) === 0 ? 12 : h % 12)}:00 {h < 12 ? 'AM' : 'PM'}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field-label">Covering the last</span>
          <select className="field-input" value={d.windowDays ?? 7}
                  onChange={(e) => set({ windowDays: Number(e.target.value) })}>
            {[1, 7, 14, 30, 90, 365].map((n) => <option key={n} value={n}>{n} days</option>)}
          </select>
        </label>
      </div>

      <label className="check-row" style={{ marginTop: 10 }}>
        <input type="checkbox" checked={d.enabled !== false}
               onChange={(e) => set({ enabled: e.target.checked })} />
        <span>Active</span>
      </label>

      <div className="row-form" style={{ marginTop: 14 }}>
        <button className="btn btn-primary" disabled={busy || !canSave} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save schedule'}
        </button>
        <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
        {!canSave && (
          <span className="dim" style={{ fontSize: 11, alignSelf: 'center' }}>
            Needs a name, at least one report, and a recipient.
          </span>
        )}
      </div>
    </Modal>
  );
}
